import React, { memo } from 'react'
import styled from 'styled-components'
import { Button, Flex, getPaletteColor, Label, Relative, Text, Tooltip } from 'pcln-design-system'
import type { ButtonProps, RelativeProps } from 'pcln-design-system'
import { Calendar, Warning } from 'pcln-icons'
import { IDateFieldProps } from '../types'

type DateFieldButtonType = ButtonProps & { borderColor?: string }

const WarningIcon = styled(Warning)`
  z-index: ${(props) => props.zIndex + 1};
`

const DateFieldButton = styled<React.FC<DateFieldButtonType>>(Button)`
  &&& {
    background-color: ${getPaletteColor('background.lightest')};
    ${(props) => (props.borderColor ? `border-color: ${getPaletteColor(props.borderColor)(props)}` : '')}
  }

  &&&:focus {
    ${(props) =>
      props.borderColor
        ? `
        box-shadow: 0 0 0 2px ${getPaletteColor(props.borderColor)(props)};
        border-color: ${getPaletteColor(props.borderColor)(props)};
      `
        : ''}
  }
`

function DateField({
  ariaDescription,
  className,
  error,
  height,
  hideTooltip = false,
  isMobile = false,
  label,
  name,
  value,
  zIndex = 3,
  onBlur,
  onClick,
  onFocus,
  ...props
}: IDateFieldProps & RelativeProps) {
  const buttonHeight = height ? height : isMobile ? 46 : 56
  const showError = !!error
  const showErrorTooltip = showError && !hideTooltip

  return (
    <Relative className={className} {...props} zIndex={zIndex}>
      <DateFieldButton
        variation='input'
        borderRadius='lg'
        py={0}
        px={2}
        height={buttonHeight}
        borderColor={showError ? 'error.base' : undefined}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        data-testid='date-field-input'
        value={value}
        name={name}
        aria-description={ariaDescription || `${value ? 'Change' : 'Select'} travel dates`}
      >
        <Flex width='100%' justifyContent='space-between' alignItems='center'>
          <Flex alignItems='center'>
            <Calendar mr={2} color='primary.base' />
            <Flex flexDirection='column'>
              {value && (
                <Label as='div' mb={0}>
                  {label}
                </Label>
              )}
              {!value && (
                <Text color='text.light' fontSize={[2, null, null, 1]}>
                  {label}
                </Text>
              )}
              {value && <Text fontSize={[2, null, null, 1]}>{value}</Text>}
            </Flex>
          </Flex>
          {showError && <WarningIcon title='warning' color='error' size={20} zIndex={zIndex} />}
        </Flex>
      </DateFieldButton>
      {showErrorTooltip && (
        <Tooltip bottom right color='error' zIndex={(Number(zIndex) ?? 3) + 2}>
          {error}
        </Tooltip>
      )}
    </Relative>
  )
}

export default memo(DateField)
